import { Injectable, signal } from '@angular/core';
import { AbstractService } from '../common/abstract.service';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { ServiceErrorHandler } from 'src/app/decorator/service-error-handler-decorator';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { PaginationParams } from '../common/pagination-params';
import { PaginatedResponse } from '../common/paginated-response';
import { ContextIqAnalysisRequest, PhysicalContextAnalysisRequest } from 'src/app/features/main-app/analysis/page/iq-context-analysis/iq-context-analysis';
import { showFeedback } from '../../util/notification';
import { TranslocoService, translate } from '@jsverse/transloco';
import moment from 'moment';
import 'moment/locale/nl';
import { GenericInfoModalComponent } from 'src/app/shared/components/modal/generic-info-modal/generic-info-modal.component';
import { modalConfig, ModalEnum } from '../../util/modalConfig';

@Injectable({
  providedIn: 'root'
})
export class ContextService extends AbstractService {
  private isAiCreditLimitReachedModalOpen = signal<boolean>(false);
  private updateContextGraphSubject = new BehaviorSubject<boolean>(false);
  public contextGraphUpdate$ = this.updateContextGraphSubject.asObservable();
  private updateContextTypeSubject = new BehaviorSubject<{flag: boolean, type: string[]}>({flag: false, type: []});
  public contextTypeUpdate$ = this.updateContextTypeSubject.asObservable();

  constructor(
    private http: HttpClient,
    private dialog: MatDialog,
    private translocoService: TranslocoService,
  ) {
    super();
  }

  /** Next month name in the current app locale (moment.js). */
  public getNextMonthName(): string {
    const lang = this.translocoService.getActiveLang()?.split('-')[0] || 'nl';
    return moment().add(1, 'month').locale(lang).format('MMMM');
  }

  setContextGraphUpdated(flag: boolean) {
    this.updateContextGraphSubject.next(flag);
  }
  
  setContextTypeUpdated(data: {flag: boolean, type: string[]}) {
    this.updateContextTypeSubject.next(data);
  }

  @ServiceErrorHandler()
  getAllContexts(pagination: PaginationParams): Observable<any> {
    return this.http.get('/api/contexts',  pagination.get());
  }
  
  @ServiceErrorHandler()
  getAllContextsSummary(params): Observable<any> {
    return this.http.get('/api/map/summary/getNetworkGraph', { params });
  }

  @ServiceErrorHandler()
  getContext(id: number): Observable<any> {
    return this.http.get(`/api/contexts/${id}`, this.httpOptions);
  }
  
  @ServiceErrorHandler()
  getContextTypes(): Observable<any> {
    return this.http.get(`/api/types?group=contexts`, this.httpOptions);
  }
  
  @ServiceErrorHandler()
  createContext(data: any): Observable<any> {
    return this.http.post(`/api/contexts`, data, this.httpOptions);
  }

  @ServiceErrorHandler()
  createBulkContext(contexts: any): Observable<any> {
    return this.http.post(`/api/batch-create/context`, contexts, this.httpOptions);
  }

  @ServiceErrorHandler()
  updateContext(id: number, data: any): Observable<any> {
    return this.http.put(`/api/contexts/${id}`, data, this.httpOptions);
  }

  @ServiceErrorHandler()
  deleteContext(id: number): Observable<any> {
    return this.http.delete(`/api/contexts/${id}`, this.httpOptions);
  }

  @ServiceErrorHandler()
  restoreContext(id: number): Observable<any> {
    return this.http.delete(`/api/contexts/${id}/restore`, this.httpOptions);
  }

  @ServiceErrorHandler()
    public getContextsRelations(): Observable<PaginatedResponse> {
      return <Observable<PaginatedResponse>> this.http.get('/api/contexts?dropdown=1', this.httpOptions);
  }
  
  @ServiceErrorHandler()
  public getIrrelevantContextItems(contextTypes?: string[]): Observable<PaginatedResponse> {
    const body: any = {};
    if (contextTypes && contextTypes.length > 0) {
      body.context_type = contextTypes;
    }
    return <Observable<PaginatedResponse>> this.http.post('/api/contexts/potential', body, this.httpOptions);
  }

  /**
   * Handles errors for AI analysis API calls
   * Shows user-friendly error messages based on HTTP status codes
   */
  public handleAiAnalysisError() {
    return catchError((error: HttpErrorResponse) => {
      const errorObject = {
        error: error,
        request: null,
      };

      switch (error.status) {
        case 500:
          // Something wrong with the AI microservice
          showFeedback(translate('error.ai_microservice_error'), 'error', errorObject, true);
          break;
        case 403:
          // Unauthorized access
          showFeedback(translate('error.ai_unauthorized'), 'error', errorObject, true);
          break;
        case 402:
          // Payment required - not enough credit: show GenericInfoModal instead of feedback
          if (!this.isAiCreditLimitReachedModalOpen()) {
            this.isAiCreditLimitReachedModalOpen.set(true);
            const month = this.getNextMonthName();
            const body = translate('contexts.ai_credit_limit_info', { month }).replace(/\n/g, '<br>');
            this.dialog.open(GenericInfoModalComponent, modalConfig(
              {
                data: {
                  title: translate('contexts.ai_credit_limit_reached'),
                  body,
                },
                disableClose: false,
                panelClass: 'customized-dialog-container'
              },
              ModalEnum.ModalSmall
            )).afterClosed().subscribe(() => {
              this.isAiCreditLimitReachedModalOpen.set(false);
            });
          }
          break;
        default:
          // For other errors, use the default error handler behavior
          showFeedback(translate('error.server_error'), 'error', errorObject, true);
          break;
      }

      return throwError(() => error);
    });
  }

  getContextAiAnalysis(data: ContextIqAnalysisRequest): Observable<any> {
    return this.http.post(`/api/iq-intelligence/context-analysis?skip_embedding_without_description=1`, data, this.httpOptions).pipe(
      this.handleAiAnalysisError()
    );
  }
  
  getPhysicalContextAiAnalysis(data: PhysicalContextAnalysisRequest): Observable<any> {
    return this.http.post(`/api/iq-intelligence/overpass-contexts`, data, this.httpOptions).pipe(
      this.handleAiAnalysisError()
    );
  }

  @ServiceErrorHandler()
  getSavedContexts(): Observable<any> {
    return this.http.get(`/api/iq-intelligence/contexts`, this.httpOptions);
  }

  @ServiceErrorHandler()
  deleteSavedContexts(): Observable<any> {
    return this.http.delete(`/api/iq-intelligence/contexts`, this.httpOptions);
  }
}
